/**
 * ==========================================================================
 * Neon Battle Royale — Global Header Controller
 * ==========================================================================
 */

(() => {
  "use strict";

  const CONTROLLER_KEY = "__NBR_HEADER_CONTROLLER__";
  const CONTROLLER_VERSION = "2.0.0";

  const SELECTORS = {
    header: "[data-nbr-header]",
    toggle: "[data-nbr-menu-toggle]",
    close: "[data-nbr-menu-close]",
    menu: "[data-nbr-mobile-menu]",
    panel: "[data-nbr-mobile-panel]",
    backdrop: "[data-nbr-menu-backdrop]",
    mobileLink: "[data-nbr-mobile-link]",
    cartLink: "[data-nbr-cart-link]",
    cartCount: "[data-nbr-cart-count]",
    mobileCartCount: "[data-nbr-mobile-cart-count]",
    cartStatus: "[data-nbr-cart-status]"
  };

  const CLASSES = {
    menuOpen: "nbr-menu-open",
    open: "is-open",
    scrolled: "is-scrolled"
  };

  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "summary",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  const MOBILE_QUERY = "(max-width: 900px)";
  const REDUCED_MOTION_QUERY =
    "(prefers-reduced-motion: reduce)";

  const SCROLL_THRESHOLD = 18;
  const CLOSE_FALLBACK_DELAY = 620;

  const existingController =
    window[CONTROLLER_KEY];

  if (
    existingController &&
    existingController.version === CONTROLLER_VERSION
  ) {
    existingController.refresh(document);
    return;
  }

  if (
    existingController &&
    typeof existingController.destroyAll === "function"
  ) {
    existingController.destroyAll();
  } else if (
    existingController &&
    typeof existingController.destroy === "function"
  ) {
    existingController.destroy();
  }

  const root = document.documentElement;

  const globalAbortController =
    new AbortController();

  const controllers = new Map();
  const openControllers = new Set();

  const mobileMedia =
    window.matchMedia(MOBILE_QUERY);

  const reducedMotionMedia =
    window.matchMedia(REDUCED_MOTION_QUERY);

  let globalScrollFrame = null;
  let globalResizeFrame = null;

  /**
   * Register an event and remove it when the signal aborts.
   *
   * @param {EventTarget|null} target
   * @param {string} eventName
   * @param {EventListenerOrEventListenerObject} handler
   * @param {AddEventListenerOptions|boolean|undefined} options
   * @param {AbortSignal|undefined} signal
   */
  function listen(
    target,
    eventName,
    handler,
    options,
    signal
  ) {
    if (
      !target ||
      typeof target.addEventListener !== "function"
    ) {
      return;
    }

    target.addEventListener(
      eventName,
      handler,
      options
    );

    if (!signal) {
      return;
    }

    signal.addEventListener(
      "abort",
      () => {
        target.removeEventListener(
          eventName,
          handler,
          options
        );
      },
      { once: true }
    );
  }

  /**
   * Support modern and legacy MediaQueryList listeners.
   *
   * @param {MediaQueryList} mediaQuery
   * @param {Function} callback
   * @param {AbortSignal} signal
   */
  function listenToMediaQuery(
    mediaQuery,
    callback,
    signal
  ) {
    if (
      typeof mediaQuery.addEventListener ===
      "function"
    ) {
      listen(
        mediaQuery,
        "change",
        callback,
        undefined,
        signal
      );

      return;
    }

    if (
      typeof mediaQuery.addListener ===
      "function"
    ) {
      mediaQuery.addListener(callback);

      signal.addEventListener(
        "abort",
        () => {
          mediaQuery.removeListener(callback);
        },
        { once: true }
      );
    }
  }

  /**
   * Safely focus without unexpectedly moving the page.
   *
   * @param {HTMLElement|null} element
   */
  function safeFocus(element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    try {
      element.focus({
        preventScroll: true
      });
    } catch (error) {
      element.focus();
    }
  }

  /**
   * Determine whether an element is currently focusable.
   *
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  function isVisibleFocusable(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    if (
      element.hidden ||
      element.closest("[hidden]") ||
      element.closest("[inert]") ||
      element.getAttribute("aria-hidden") === "true"
    ) {
      return false;
    }

    const styles =
      window.getComputedStyle(element);

    if (
      styles.display === "none" ||
      styles.visibility === "hidden"
    ) {
      return false;
    }

    return (
      element.offsetWidth > 0 ||
      element.offsetHeight > 0 ||
      element.getClientRects().length > 0
    );
  }

  /**
   * Return focusable descendants in visual order.
   *
   * @param {HTMLElement|null} container
   * @returns {HTMLElement[]}
   */
  function getFocusableElements(container) {
    if (!container) {
      return [];
    }

    return Array.from(
      container.querySelectorAll(
        FOCUSABLE_SELECTOR
      )
    ).filter(isVisibleFocusable);
  }

  /**
   * Discover header roots inside a supplied container.
   *
   * @param {Document|DocumentFragment|Element} container
   * @returns {HTMLElement[]}
   */
  function getHeaderRoots(
    container = document
  ) {
    const headers = [];

    if (
      container &&
      container.nodeType === 1 &&
      typeof container.matches === "function" &&
      container.matches(SELECTORS.header)
    ) {
      headers.push(container);
    }

    if (
      container &&
      typeof container.querySelectorAll ===
        "function"
    ) {
      headers.push(
        ...container.querySelectorAll(
          SELECTORS.header
        )
      );
    }

    return Array.from(
      new Set(headers)
    ).filter(
      (header) =>
        header instanceof HTMLElement
    );
  }

  /**
   * Keep document scroll locking accurate when headers reload.
   */
  function synchroniseDocumentLock() {
    const shouldLock =
      openControllers.size > 0;

    root.classList.toggle(
      CLASSES.menuOpen,
      shouldLock
    );

    document.body?.classList.toggle(
      CLASSES.menuOpen,
      shouldLock
    );

    root.dataset.nbrMenuOpen =
      String(shouldLock);
  }

  /**
   * Close any other drawer before opening a new one.
   *
   * @param {NBRHeaderController} activeController
   */
  function closeOtherMenus(
    activeController
  ) {
    Array.from(openControllers).forEach(
      (controller) => {
        if (controller !== activeController) {
          controller.closeMenu({
            restoreFocus: false,
            immediate: true
          });
        }
      }
    );
  }

  /**
   * Extract a cart count from commonly used custom-event payloads.
   *
   * @param {CustomEvent|Event} event
   * @returns {number|null}
   */
  function extractCartCount(event) {
    const detail =
      event && "detail" in event
        ? event.detail
        : null;

    const candidates = [
      detail?.cart?.item_count,
      detail?.cart?.itemCount,
      detail?.item_count,
      detail?.itemCount,
      detail?.cartCount,
      detail?.count
    ];

    const match = candidates.find(
      (candidate) =>
        Number.isFinite(Number(candidate))
    );

    if (match === undefined) {
      return null;
    }

    return Math.max(
      0,
      Math.round(Number(match))
    );
  }

  class NBRHeaderController {
    /**
     * @param {HTMLElement} header
     */
    constructor(header) {
      this.header = header;

      this.toggle =
        header.querySelector(
          SELECTORS.toggle
        );

      this.closeButton =
        header.querySelector(
          SELECTORS.close
        );

      this.menu =
        header.querySelector(
          SELECTORS.menu
        );

      this.panel =
        header.querySelector(
          SELECTORS.panel
        );

      this.backdrop =
        header.querySelector(
          SELECTORS.backdrop
        );

      this.cartLink =
        header.querySelector(
          SELECTORS.cartLink
        );

      this.cartCount =
        header.querySelector(
          SELECTORS.cartCount
        );

      this.mobileCartCount =
        header.querySelector(
          SELECTORS.mobileCartCount
        );

      this.cartStatus =
        header.querySelector(
          SELECTORS.cartStatus
        );

      this.abortController =
        new AbortController();

      this.signal =
        this.abortController.signal;

      this.isOpen = false;
      this.isDestroyed = false;

      this.closeTimer = null;
      this.closeTransitionHandler = null;

      this.openFrameOne = null;
      this.openFrameTwo = null;

      this.lastFocusedElement = null;

      this.openLabel =
        this.toggle?.dataset.nbrOpenLabel ||
        this.toggle?.getAttribute(
          "aria-label"
        ) ||
        "Open navigation menu";

      this.closeLabel =
        this.toggle?.dataset.nbrCloseLabel ||
        this.closeButton?.getAttribute(
          "aria-label"
        ) ||
        "Close navigation menu";

      const initialCartLabel =
        this.cartLink?.getAttribute(
          "aria-label"
        ) || "Open cart";

      this.cartBaseLabel =
        initialCartLabel
          .split(":")[0]
          .trim() ||
        "Open cart";

      this.hasDrawer = Boolean(
        this.toggle &&
        this.menu &&
        this.panel &&
        this.backdrop
      );

      this.bindEvents();
      this.synchroniseInitialState();
      this.updateScrollState();
      this.updateMeasurements();
    }

    bindEvents() {
      if (this.hasDrawer) {
        listen(
          this.toggle,
          "click",
          () => {
            this.toggleMenu();
          },
          undefined,
          this.signal
        );

        listen(
          this.closeButton,
          "click",
          () => {
            this.closeMenu({
              restoreFocus: true
            });
          },
          undefined,
          this.signal
        );

        listen(
          this.backdrop,
          "click",
          () => {
            this.closeMenu({
              restoreFocus: true
            });
          },
          undefined,
          this.signal
        );

        listen(
          this.menu,
          "click",
          (event) => {
            const target =
              event.target instanceof Element
                ? event.target.closest(
                    SELECTORS.mobileLink
                  )
                : null;

            if (!target) {
              return;
            }

            this.closeMenu({
              restoreFocus: false,
              immediate: true
            });
          },
          undefined,
          this.signal
        );

        listen(
          document,
          "keydown",
          (event) => {
            this.handleKeydown(event);
          },
          undefined,
          this.signal
        );

        listen(
          document,
          "focusin",
          (event) => {
            this.handleFocusIn(event);
          },
          undefined,
          this.signal
        );
      }

      listen(
        document,
        "shopify:section:select",
        (event) => {
          if (
            event.target === this.header ||
            (
              event.target instanceof Element &&
              event.target.contains(this.header)
            )
          ) {
            this.refresh();
          }
        },
        undefined,
        this.signal
      );
    }

    synchroniseInitialState() {
      this.isOpen = false;

      this.header.dataset.nbrMenuState =
        "closed";

      if (!this.hasDrawer) {
        return;
      }

      this.toggle.setAttribute(
        "aria-expanded",
        "false"
      );

      this.toggle.setAttribute(
        "aria-label",
        this.openLabel
      );

      this.menu.setAttribute(
        "aria-hidden",
        "true"
      );

      this.menu.classList.remove(
        CLASSES.open
      );

      this.backdrop.classList.remove(
        CLASSES.open
      );

      this.setMenuInert(true);

      this.menu.hidden = true;
      this.backdrop.hidden = true;

      openControllers.delete(this);
      synchroniseDocumentLock();
    }

    /**
     * @param {boolean} inert
     */
    setMenuInert(inert) {
      if (!this.menu) {
        return;
      }

      if ("inert" in this.menu) {
        this.menu.inert = inert;
      }

      if (inert) {
        this.menu.setAttribute(
          "data-nbr-inert",
          "true"
        );
      } else {
        this.menu.removeAttribute(
          "data-nbr-inert"
        );
      }
    }

    toggleMenu() {
      if (this.isOpen) {
        this.closeMenu({
          restoreFocus: true
        });

        return;
      }

      this.openMenu();
    }

    openMenu() {
      if (
        !this.hasDrawer ||
        this.isOpen ||
        this.isDestroyed ||
        !mobileMedia.matches
      ) {
        return;
      }

      closeOtherMenus(this);
      this.clearCloseTransition();

      this.lastFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : this.toggle;

      this.isOpen = true;

      this.header.dataset.nbrMenuState =
        "opening";

      this.menu.hidden = false;
      this.backdrop.hidden = false;

      this.setMenuInert(false);

      this.menu.setAttribute(
        "aria-hidden",
        "false"
      );

      this.toggle.setAttribute(
        "aria-expanded",
        "true"
      );

      this.toggle.setAttribute(
        "aria-label",
        this.closeLabel
      );

      openControllers.add(this);
      synchroniseDocumentLock();

      this.cancelOpeningFrames();

      this.openFrameOne =
        window.requestAnimationFrame(
          () => {
            this.openFrameOne = null;

            this.openFrameTwo =
              window.requestAnimationFrame(
                () => {
                  this.openFrameTwo = null;

                  if (
                    !this.isOpen ||
                    this.isDestroyed
                  ) {
                    return;
                  }

                  this.menu.classList.add(
                    CLASSES.open
                  );

                  this.backdrop.classList.add(
                    CLASSES.open
                  );

                  this.header.dataset.nbrMenuState =
                    "open";

                  safeFocus(
                    this.getInitialFocusTarget()
                  );

                  this.header.dispatchEvent(
                    new CustomEvent(
                      "nbr:menu-open",
                      {
                        bubbles: true,
                        detail: {
                          sectionId:
                            this.header.dataset
                              .sectionId || ""
                        }
                      }
                    )
                  );
                }
              );
          }
        );
    }

    /**
     * @param {{
     *   restoreFocus?: boolean,
     *   immediate?: boolean
     * }} options
     */
    closeMenu(options = {}) {
      const {
        restoreFocus = true,
        immediate = false
      } = options;

      if (
        !this.hasDrawer ||
        this.isDestroyed
      ) {
        return;
      }

      if (
        !this.isOpen &&
        !immediate
      ) {
        return;
      }

      this.clearCloseTransition();
      this.cancelOpeningFrames();

      const focusTarget =
        this.lastFocusedElement ||
        this.toggle;

      this.isOpen = false;

      this.header.dataset.nbrMenuState =
        immediate
          ? "closed"
          : "closing";

      if (restoreFocus) {
        safeFocus(focusTarget);
      }

      this.toggle.setAttribute(
        "aria-expanded",
        "false"
      );

      this.toggle.setAttribute(
        "aria-label",
        this.openLabel
      );

      this.menu.setAttribute(
        "aria-hidden",
        "true"
      );

      this.menu.classList.remove(
        CLASSES.open
      );

      this.backdrop.classList.remove(
        CLASSES.open
      );

      this.setMenuInert(true);

      openControllers.delete(this);
      synchroniseDocumentLock();

      const finishClosing = () => {
        if (
          this.isOpen ||
          this.isDestroyed
        ) {
          return;
        }

        this.menu.hidden = true;
        this.backdrop.hidden = true;

        this.header.dataset.nbrMenuState =
          "closed";

        this.lastFocusedElement = null;

        this.header.dispatchEvent(
          new CustomEvent(
            "nbr:menu-close",
            {
              bubbles: true,
              detail: {
                sectionId:
                  this.header.dataset
                    .sectionId || ""
              }
            }
          )
        );
      };

      if (
        immediate ||
        reducedMotionMedia.matches
      ) {
        finishClosing();
        return;
      }

      this.closeTransitionHandler =
        (event) => {
          if (
            event.target !== this.panel ||
            event.propertyName !==
              "transform"
          ) {
            return;
          }

          this.clearCloseTransition();
          finishClosing();
        };

      this.panel.addEventListener(
        "transitionend",
        this.closeTransitionHandler
      );

      this.closeTimer =
        window.setTimeout(
          () => {
            this.clearCloseTransition();
            finishClosing();
          },
          CLOSE_FALLBACK_DELAY
        );
    }

    clearCloseTransition() {
      window.clearTimeout(
        this.closeTimer
      );

      this.closeTimer = null;

      if (
        this.closeTransitionHandler &&
        this.panel
      ) {
        this.panel.removeEventListener(
          "transitionend",
          this.closeTransitionHandler
        );
      }

      this.closeTransitionHandler =
        null;
    }

    cancelOpeningFrames() {
      if (this.openFrameOne !== null) {
        window.cancelAnimationFrame(
          this.openFrameOne
        );

        this.openFrameOne = null;
      }

      if (this.openFrameTwo !== null) {
        window.cancelAnimationFrame(
          this.openFrameTwo
        );

        this.openFrameTwo = null;
      }
    }

    /**
     * @returns {HTMLElement}
     */
    getInitialFocusTarget() {
      if (
        this.closeButton instanceof
        HTMLElement
      ) {
        return this.closeButton;
      }

      const focusableElements =
        getFocusableElements(this.panel);

      return (
        focusableElements[0] ||
        this.panel
      );
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();

        this.closeMenu({
          restoreFocus: true
        });

        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      this.trapFocus(event);
    }

    /**
     * Keep programmatic focus inside the open dialog.
     *
     * @param {FocusEvent} event
     */
    handleFocusIn(event) {
      if (
        !this.isOpen ||
        !this.panel ||
        this.panel.contains(event.target)
      ) {
        return;
      }

      safeFocus(
        this.getInitialFocusTarget()
      );
    }

    /**
     * @param {KeyboardEvent} event
     */
    trapFocus(event) {
      const focusableElements =
        getFocusableElements(this.panel);

      if (focusableElements.length === 0) {
        event.preventDefault();
        safeFocus(this.panel);
        return;
      }

      const firstElement =
        focusableElements[0];

      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      const activeElement =
        document.activeElement;

      if (
        event.shiftKey &&
        (
          activeElement === firstElement ||
          !this.panel.contains(
            activeElement
          )
        )
      ) {
        event.preventDefault();
        safeFocus(lastElement);
        return;
      }

      if (
        !event.shiftKey &&
        activeElement === lastElement
      ) {
        event.preventDefault();
        safeFocus(firstElement);
      }
    }

    updateScrollState() {
      if (this.isDestroyed) {
        return;
      }

      const isScrolled =
        window.scrollY >
        SCROLL_THRESHOLD;

      this.header.classList.toggle(
        CLASSES.scrolled,
        isScrolled
      );

      this.header.dataset.nbrHeaderScrolled =
        String(isScrolled);
    }

    updateMeasurements() {
      if (
        this.isDestroyed ||
        !this.header.isConnected
      ) {
        return;
      }

      const height =
        this.header.getBoundingClientRect()
          .height;

      if (height <= 0) {
        return;
      }

      const roundedHeight =
        `${Math.round(height)}px`;

      this.header.style.setProperty(
        "--header-measured-height",
        roundedHeight
      );

      root.style.setProperty(
        "--nbr-header-height-current",
        roundedHeight
      );

      if (mobileMedia.matches) {
        root.style.setProperty(
          "--nbr-header-height-mobile",
          roundedHeight
        );
      } else {
        root.style.setProperty(
          "--nbr-header-height",
          roundedHeight
        );
      }
    }

    handleViewportChange() {
      if (this.isDestroyed) {
        return;
      }

      this.updateScrollState();
      this.updateMeasurements();

      if (
        !mobileMedia.matches &&
        this.isOpen
      ) {
        this.closeMenu({
          restoreFocus: false,
          immediate: true
        });
      }
    }

    /**
     * Update all visual and accessible cart counters.
     *
     * @param {number} count
     */
    updateCartCount(count) {
      const safeCount = Math.max(
        0,
        Math.round(Number(count) || 0)
      );

      const visibleCount =
        safeCount > 99
          ? "99+"
          : String(safeCount);

      if (this.cartCount) {
        this.cartCount.textContent =
          visibleCount;

        this.cartCount.classList.toggle(
          "is-empty",
          safeCount === 0
        );
      }

      if (this.mobileCartCount) {
        this.mobileCartCount.textContent =
          `(${safeCount})`;
      }

      const itemWord =
        safeCount === 1
          ? "item"
          : "items";

      const accessibleLabel =
        `${this.cartBaseLabel}: ` +
        `${safeCount} ${itemWord}`;

      if (this.cartLink) {
        this.cartLink.setAttribute(
          "aria-label",
          accessibleLabel
        );
      }

      if (this.cartStatus) {
        this.cartStatus.textContent =
          accessibleLabel;
      }
    }

    refresh() {
      if (this.isDestroyed) {
        return;
      }

      this.updateScrollState();
      this.updateMeasurements();

      if (
        !mobileMedia.matches &&
        this.isOpen
      ) {
        this.closeMenu({
          restoreFocus: false,
          immediate: true
        });
      }
    }

    destroy() {
      if (this.isDestroyed) {
        return;
      }

      if (this.isOpen) {
        this.closeMenu({
          restoreFocus: false,
          immediate: true
        });
      }

      this.isDestroyed = true;

      this.clearCloseTransition();
      this.cancelOpeningFrames();

      this.abortController.abort();

      openControllers.delete(this);
      synchroniseDocumentLock();

      controllers.delete(this.header);

      delete this.header
        .dataset.nbrHeaderScrolled;
    }
  }

  function initialiseHeader(header) {
    if (
      !(header instanceof HTMLElement)
    ) {
      return null;
    }

    if (controllers.has(header)) {
      return controllers.get(header);
    }

    const controller =
      new NBRHeaderController(header);

    controllers.set(
      header,
      controller
    );

    return controller;
  }

  function initialiseWithin(
    container = document
  ) {
    getHeaderRoots(container).forEach(
      initialiseHeader
    );
  }

  function refreshWithin(
    container = document
  ) {
    initialiseWithin(container);

    getHeaderRoots(container).forEach(
      (header) => {
        controllers
          .get(header)
          ?.refresh();
      }
    );
  }

  function destroyWithin(container) {
    Array.from(
      controllers.entries()
    ).forEach(
      ([header, controller]) => {
        if (
          header === container ||
          (
            container instanceof Element &&
            container.contains(header)
          )
        ) {
          controller.destroy();
        }
      }
    );
  }

  function requestGlobalScrollUpdate() {
    if (globalScrollFrame !== null) {
      return;
    }

    globalScrollFrame =
      window.requestAnimationFrame(
        () => {
          globalScrollFrame = null;

          controllers.forEach(
            (controller) => {
              controller.updateScrollState();
            }
          );
        }
      );
  }

  function requestGlobalResizeUpdate() {
    if (globalResizeFrame !== null) {
      return;
    }

    globalResizeFrame =
      window.requestAnimationFrame(
        () => {
          globalResizeFrame = null;

          controllers.forEach(
            (controller) => {
              controller.handleViewportChange();
            }
          );
        }
      );
  }

  function handleCartEvent(event) {
    const count =
      extractCartCount(event);

    if (count === null) {
      return;
    }

    controllers.forEach(
      (controller) => {
        controller.updateCartCount(count);
      }
    );
  }

  function bindGlobalEvents() {
    const signal =
      globalAbortController.signal;

    listen(
      window,
      "scroll",
      requestGlobalScrollUpdate,
      { passive: true },
      signal
    );

    listen(
      window,
      "resize",
      requestGlobalResizeUpdate,
      { passive: true },
      signal
    );

    listen(
      window,
      "orientationchange",
      requestGlobalResizeUpdate,
      { passive: true },
      signal
    );

    listenToMediaQuery(
      mobileMedia,
      requestGlobalResizeUpdate,
      signal
    );

    [
      "nbr:cart-updated",
      "nbr:cart:updated",
      "cart:updated",
      "shopify:cart:updated"
    ].forEach((eventName) => {
      listen(
        document,
        eventName,
        handleCartEvent,
        undefined,
        signal
      );
    });

    listen(
      document,
      "shopify:section:load",
      (event) => {
        refreshWithin(event.target);
      },
      undefined,
      signal
    );

    listen(
      document,
      "shopify:section:unload",
      (event) => {
        destroyWithin(event.target);
      },
      undefined,
      signal
    );

    listen(
      document,
      "shopify:section:reorder",
      () => {
        refreshWithin(document);
      },
      undefined,
      signal
    );
  }

  const controller = {
    version: CONTROLLER_VERSION,

    refresh(
      container = document
    ) {
      refreshWithin(container);
    },

    destroy(
      container = document
    ) {
      destroyWithin(container);
    },

    updateCartCount(count) {
      controllers.forEach(
        (headerController) => {
          headerController.updateCartCount(
            count
          );
        }
      );
    },

    closeAll() {
      Array.from(
        openControllers
      ).forEach(
        (headerController) => {
          headerController.closeMenu({
            restoreFocus: false,
            immediate: true
          });
        }
      );
    },

    destroyAll() {
      globalAbortController.abort();

      Array.from(
        controllers.values()
      ).forEach(
        (headerController) => {
          headerController.destroy();
        }
      );

      controllers.clear();
      openControllers.clear();

      synchroniseDocumentLock();

      if (globalScrollFrame !== null) {
        window.cancelAnimationFrame(
          globalScrollFrame
        );

        globalScrollFrame = null;
      }

      if (globalResizeFrame !== null) {
        window.cancelAnimationFrame(
          globalResizeFrame
        );

        globalResizeFrame = null;
      }

      if (
        window[CONTROLLER_KEY] ===
        controller
      ) {
        delete window[CONTROLLER_KEY];
      }
    }
  };

  window[CONTROLLER_KEY] =
    controller;

  function start() {
    bindGlobalEvents();
    initialiseWithin(document);
    requestGlobalScrollUpdate();
    requestGlobalResizeUpdate();
  }

  if (
    document.readyState === "loading"
  ) {
    listen(
      document,
      "DOMContentLoaded",
      start,
      { once: true },
      globalAbortController.signal
    );
  } else {
    start();
  }
})();